import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { AuditFindingsTab } from '../features/audits/AuditFindingsTab';
import { AuditStatusStepper } from '../features/audits/AuditStatusStepper';
import {
  addChecklistItem,
  addTeamMember,
  createFinding,
  escalateFindingToNc,
  promoteFindingToRisk,
  fetchAudit,
  fetchAuditReport,
  loadChecklistTemplate,
  removeTeamMember,
  updateAudit,
  updateChecklistItem,
} from '../features/audits/auditsApi';
import type {
  AuditDetail,
  AuditReport,
  AuditStatus,
  AuditTeamRole,
  FindingType,
} from '../features/audits/types';
import {
  AUDIT_STATUS_LABEL,
  AUDIT_TYPE_LABEL,
  CHECKLIST_RESULT_LABEL,
  TEAM_ROLE_LABEL,
  auditStatusBadgeClass,
  checklistResultBadgeClass,
} from '../features/audits/auditUtils';
import { fetchUsers } from '../features/users/usersApi';
import { DateCell } from '../components/DateCell';
import { ExportPdfButton } from '../features/reports/ExportPdfButton';
import { fetchAuditDraft } from '../features/ai/aiApi';

type TabId = 'datos' | 'equipo' | 'checklist' | 'hallazgos' | 'informe';

const TABS: { id: TabId; label: string }[] = [
  { id: 'datos', label: 'Datos' },
  { id: 'equipo', label: 'Equipo' },
  { id: 'checklist', label: 'Checklist' },
  { id: 'hallazgos', label: 'Hallazgos' },
  { id: 'informe', label: 'Informe' },
];

const NEXT_STATUS: Partial<Record<AuditStatus, AuditStatus>> = {
  planificada: 'en_curso',
  en_curso: 'informe_emitido',
  informe_emitido: 'cerrada',
};

const FINDING_TYPES: FindingType[] = [
  'no_conformidad',
  'observacion',
  'oportunidad',
  'conformidad',
];

const TEAM_ROLES: AuditTeamRole[] = ['lider', 'auditor', 'observador'];

export function AuditDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { canEdit } = usePlatformAccessLevel('audits.access');
  const [audit, setAudit] = useState<AuditDetail | null>(null);
  const [report, setReport] = useState<AuditReport | null>(null);
  const [tab, setTab] = useState<TabId>('datos');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [escalatingId, setEscalatingId] = useState<string | null>(null);
  const [promotingId, setPromotingId] = useState<string | null>(null);
  const [teamUserId, setTeamUserId] = useState('');
  const [teamExternalName, setTeamExternalName] = useState('');
  const [teamRole, setTeamRole] = useState<AuditTeamRole>('auditor');
  const [teamError, setTeamError] = useState<string | null>(null);
  const [users, setUsers] = useState<Array<{ id: number; email: string }>>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [findingType, setFindingType] = useState<FindingType>('observacion');
  const [findingSeverity, setFindingSeverity] = useState<'mayor' | 'menor'>('menor');
  const [findingDescription, setFindingDescription] = useState('');
  const [findingClause, setFindingClause] = useState('');
  const [checklistClause, setChecklistClause] = useState('');
  const [checklistQuestion, setChecklistQuestion] = useState('');
  const [checklistError, setChecklistError] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [draftingReport, setDraftingReport] = useState(false);
  const [aiDraftText, setAiDraftText] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAudit(id);
      setAudit(data);
    } catch {
      setError('No se pudo cargar la auditoría.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadReport = useCallback(async () => {
    if (!id) return;
    try {
      const data = await fetchAuditReport(id);
      setReport(data);
    } catch {
      setReport(null);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (tab === 'informe') {
      void loadReport();
    }
  }, [tab, loadReport]);

  useEffect(() => {
    if (!canEdit || audit?.type !== 'interna') return;
    setUsersLoading(true);
    void fetchUsers({ pageSize: 200 })
      .then((res) => setUsers(res.data.map((u) => ({ id: u.id, email: u.email }))))
      .catch(() => setUsers([]))
      .finally(() => setUsersLoading(false));
  }, [canEdit, audit?.type]);

  const handleAdvanceStatus = async () => {
    if (!audit || !id) return;
    const next = NEXT_STATUS[audit.status];
    if (!next) return;
    setSubmitting(true);
    setStatusError(null);
    try {
      await updateAudit(id, { status: next });
      await load();
    } catch (err) {
      const message =
        axios.isAxiosError(err) && typeof err.response?.data?.error === 'string'
          ? err.response.data.error
          : 'No se pudo avanzar el estado de la auditoría.';
      setStatusError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLoadChecklist = async () => {
    if (!id) return;
    setSubmitting(true);
    try {
      await loadChecklistTemplate(id);
      await load();
      setTab('checklist');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChecklistResult = async (itemId: string, result: string) => {
    setSubmitting(true);
    try {
      await updateChecklistItem(itemId, { result: result || null });
      await load();
    } finally {
      setSubmitting(false);
    }
  };

  const handleChecklistEvidence = async (itemId: string, evidence: string) => {
    try {
      await updateChecklistItem(itemId, { evidence: evidence.trim() || null });
      await load();
    } catch {
      setChecklistError('No se pudo guardar la evidencia.');
    }
  };

  const handleAddChecklistItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !checklistClause.trim() || !checklistQuestion.trim()) return;
    setSubmitting(true);
    setChecklistError(null);
    try {
      await addChecklistItem(id, {
        clause: checklistClause.trim(),
        question: checklistQuestion.trim(),
      });
      setChecklistClause('');
      setChecklistQuestion('');
      await load();
    } catch (err) {
      const message =
        axios.isAxiosError(err) && typeof err.response?.data?.error === 'string'
          ? err.response.data.error
          : 'No se pudo agregar el ítem al checklist.';
      setChecklistError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !audit) return;
    const isExternal = audit.type === 'externa';
    if (isExternal ? !teamExternalName.trim() : !teamUserId) return;
    setSubmitting(true);
    setTeamError(null);
    try {
      await addTeamMember(
        id,
        isExternal
          ? { externalName: teamExternalName.trim(), role: teamRole }
          : { userId: Number(teamUserId), role: teamRole },
      );
      setTeamUserId('');
      setTeamExternalName('');
      setTeamRole('auditor');
      await load();
    } catch (err) {
      const message =
        axios.isAxiosError(err) && typeof err.response?.data?.error === 'string'
          ? err.response.data.error
          : err instanceof Error
            ? err.message
            : 'No se pudo agregar el miembro al equipo.';
      setTeamError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveTeam = async (memberId: string) => {
    await removeTeamMember(memberId);
    await load();
  };

  const handleAddFinding = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !findingDescription.trim()) return;
    setSubmitting(true);
    try {
      await createFinding(id, {
        type: findingType,
        severity: findingType === 'no_conformidad' ? findingSeverity : null,
        clause: findingClause.trim() || null,
        description: findingDescription.trim(),
      });
      setFindingDescription('');
      setFindingClause('');
      await load();
      setTab('hallazgos');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEscalate = async (findingId: string) => {
    setEscalatingId(findingId);
    try {
      await escalateFindingToNc(findingId);
      await load();
    } finally {
      setEscalatingId(null);
    }
  };

  const handlePromoteToRisk = async (findingId: string) => {
    setPromotingId(findingId);
    try {
      await promoteFindingToRisk(findingId);
      await load();
    } finally {
      setPromotingId(null);
    }
  };

  if (loading) {
    return <div className="p-6 text-muted-foreground">Cargando auditoría…</div>;
  }

  if (error || !audit) {
    return (
      <div className="p-6">
        <p className="text-destructive">{error ?? 'Auditoría no encontrada.'}</p>
        <Link to="/auditorias" className="text-primary text-sm hover:underline">
          Volver al listado
        </Link>
      </div>
    );
  }

  const nextStatus = NEXT_STATUS[audit.status];
  const isExternalAudit = audit.type === 'externa';
  const teamUserIds = new Set(
    audit.team.map((m) => m.userId).filter((userId): userId is number => userId != null),
  );
  const existingExternalNames = new Set(
    audit.team
      .map((m) => m.externalName?.trim().toLowerCase())
      .filter((name): name is string => Boolean(name)),
  );
  const availableTeamUsers = users.filter((u) => !teamUserIds.has(u.id));
  const checklistTotal = audit.checklistItems.length;
  const checklistEvaluated = audit.checklistItems.filter((item) => item.result).length;
  const checklistPercent =
    checklistTotal > 0 ? Math.round((checklistEvaluated / checklistTotal) * 100) : 0;
  const isAuditClosed = audit.status === 'cerrada';

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to="/auditorias" className="text-sm text-primary hover:underline">
            ← Auditorías
          </Link>
          <h1 className="text-2xl font-bold mt-1">{audit.scope.slice(0, 120)}</h1>
          <div className="flex flex-wrap gap-2 mt-2">
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded ${auditStatusBadgeClass(audit.status)}`}
            >
              {AUDIT_STATUS_LABEL[audit.status]}
            </span>
            <span className="text-xs text-muted-foreground">{AUDIT_TYPE_LABEL[audit.type]}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canEdit && (
            <button
              type="button"
              disabled={draftingReport}
              className="rounded-md border border-primary px-3 py-1.5 text-sm text-primary disabled:opacity-50"
              onClick={async () => {
                setDraftingReport(true);
                try {
                  const result = await fetchAuditDraft({ auditId: id });
                  const text =
                    (result.draft.executiveSummary as string | undefined) ??
                    (result.draft.summary as string | undefined) ??
                    result.text;
                  setAiDraftText(text.trim());
                  setTab('informe');
                } catch {
                  setError('No se pudo generar el borrador IA del informe.');
                } finally {
                  setDraftingReport(false);
                }
              }}
            >
              {draftingReport ? 'Generando borrador…' : 'Borrador IA'}
            </button>
          )}
          <ExportPdfButton
            reportType="audit"
            resourceId={id}
            permissionKey="audits.access"
          />
        </div>
      </div>

      <AuditStatusStepper
        status={audit.status}
        canAdvance={canEdit && Boolean(nextStatus) && !isAuditClosed}
        advancing={submitting}
        nextStatus={nextStatus}
        onAdvance={() => void handleAdvanceStatus()}
      />
      {statusError && <p className="text-sm text-destructive">{statusError}</p>}

      <div className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`px-4 py-2 text-sm border-b-2 -mb-px ${
              tab === t.id
                ? 'border-primary text-primary font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'datos' && (
        <div className="grid gap-4 md:grid-cols-2 text-sm">
          <div>
            <p className="text-muted-foreground">Criterios</p>
            <p>{audit.criteria}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Área auditada</p>
            <p>{audit.auditeeArea ?? '—'}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Fecha planificada</p>
            <DateCell value={audit.plannedDate} />
          </div>
          <div>
            <p className="text-muted-foreground">Programa</p>
            <p>{audit.program?.year ?? '—'}</p>
          </div>
          {audit.externalEntity && (
            <div>
              <p className="text-muted-foreground">Entidad externa</p>
              <p>{audit.externalEntity}</p>
            </div>
          )}
        </div>
      )}

      {tab === 'equipo' && (
        <div className="space-y-4">
          <ul className="space-y-2 text-sm">
            {audit.team.length === 0 ? (
              <li className="text-muted-foreground">Sin miembros en el equipo.</li>
            ) : (
              audit.team.map((m) => (
                <li
                  key={m.id}
                  className="flex items-center justify-between border border-border rounded-md px-3 py-2"
                >
                  <span>
                    {m.externalName ?? m.user?.email ?? (m.userId ? `Usuario #${m.userId}` : '—')} —{' '}
                    {TEAM_ROLE_LABEL[m.role]}
                  </span>
                  {canEdit && (
                    <button
                      type="button"
                      className="text-destructive text-xs hover:underline"
                      onClick={() => void handleRemoveTeam(m.id)}
                    >
                      Quitar
                    </button>
                  )}
                </li>
              ))
            )}
          </ul>
          {canEdit && (
            <form className="flex flex-wrap gap-2 items-end" onSubmit={(e) => void handleAddTeam(e)}>
              {isExternalAudit ? (
                <label className="text-sm min-w-[220px] flex-1">
                  Nombre del auditor / miembro
                  <input
                    type="text"
                    className="block mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
                    value={teamExternalName}
                    onChange={(e) => {
                      setTeamExternalName(e.target.value);
                      setTeamError(null);
                    }}
                    placeholder="Ej: Juan Pérez — organismo certificador"
                    maxLength={255}
                    required
                  />
                </label>
              ) : (
                <label className="text-sm min-w-[220px] flex-1">
                  Usuario
                  <select
                    className="block mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
                    value={teamUserId}
                    onChange={(e) => {
                      setTeamUserId(e.target.value);
                      setTeamError(null);
                    }}
                    disabled={usersLoading || availableTeamUsers.length === 0}
                    required
                  >
                    <option value="">
                      {usersLoading
                        ? 'Cargando usuarios…'
                        : availableTeamUsers.length === 0
                          ? 'No hay usuarios disponibles'
                          : 'Seleccionar usuario…'}
                    </option>
                    {availableTeamUsers.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.email}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label className="text-sm">
                Rol
                <select
                  className="block mt-1 rounded-md border border-border px-3 py-2 text-sm"
                  value={teamRole}
                  onChange={(e) => setTeamRole(e.target.value as AuditTeamRole)}
                >
                  {TEAM_ROLES.map((role) => (
                    <option key={role} value={role}>
                      {TEAM_ROLE_LABEL[role]}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="submit"
                className="btn btn-outline"
                disabled={
                  submitting ||
                  (isExternalAudit
                    ? !teamExternalName.trim() ||
                      existingExternalNames.has(teamExternalName.trim().toLowerCase())
                    : !teamUserId || usersLoading)
                }
              >
                Agregar
              </button>
              {isExternalAudit && (
                <p className="w-full text-xs text-muted-foreground">
                  En auditorías externas el equipo suele ser de fuera de la organización; ingrese el
                  nombre del auditor o participante.
                </p>
              )}
              {teamError && <p className="w-full text-sm text-destructive">{teamError}</p>}
            </form>
          )}
        </div>
      )}

      {tab === 'checklist' && (
        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm space-y-2">
            <h3 className="font-medium">Cómo avanzar con el checklist</h3>
            <ol className="list-decimal pl-5 space-y-1 text-muted-foreground">
              <li>Cargá la plantilla ISO 9001 o agregá ítems manualmente (cláusula + pregunta).</li>
              <li>En cada ítem elegí el resultado: Conforme, No conforme o No aplica.</li>
              <li>Registrá evidencia opcional y, si hay desvíos, un hallazgo en la pestaña Hallazgos.</li>
              <li>Cuando termines la ejecución, avanzá el estado a «En curso» → «Informe emitido» → «Cerrada».</li>
            </ol>
          </div>

          {checklistTotal > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Progreso del checklist</span>
                <span className="font-medium">
                  {checklistEvaluated}/{checklistTotal} ({checklistPercent}%)
                </span>
              </div>
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${checklistPercent}%` }}
                />
              </div>
            </div>
          )}

          {canEdit && !isAuditClosed && checklistTotal === 0 && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="btn btn-outline btn-sm"
                disabled={submitting}
                onClick={() => void handleLoadChecklist()}
              >
                Cargar plantilla ISO 9001
              </button>
            </div>
          )}

          {canEdit && !isAuditClosed && (
            <form
              className="border border-border rounded-lg p-4 space-y-3"
              onSubmit={(e) => void handleAddChecklistItem(e)}
            >
              <h3 className="font-medium text-sm">Agregar ítem al checklist</h3>
              <div className="flex flex-wrap gap-3">
                <input
                  className="rounded-md border border-border px-3 py-2 text-sm w-28"
                  placeholder="Cláusula"
                  value={checklistClause}
                  onChange={(e) => setChecklistClause(e.target.value)}
                  maxLength={40}
                  required
                />
                <input
                  className="rounded-md border border-border px-3 py-2 text-sm flex-1 min-w-[200px]"
                  placeholder="Pregunta o criterio a verificar"
                  value={checklistQuestion}
                  onChange={(e) => setChecklistQuestion(e.target.value)}
                  maxLength={10000}
                  required
                />
                <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>
                  Agregar ítem
                </button>
              </div>
              {checklistError && <p className="text-sm text-destructive">{checklistError}</p>}
            </form>
          )}

          {audit.checklistItems.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Checklist vacío. Cargá la plantilla ISO 9001 o agregá ítems manualmente.
            </p>
          ) : (
            <div className="space-y-3">
              {audit.checklistItems.map((item) => (
                <div key={item.id} className="border border-border rounded-lg p-3 text-sm space-y-2">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <p className="font-medium">
                      {item.clause} — {item.question}
                    </p>
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded ${checklistResultBadgeClass(item.result)}`}
                    >
                      {item.result ? CHECKLIST_RESULT_LABEL[item.result] : 'Pendiente'}
                    </span>
                  </div>
                  {canEdit && !isAuditClosed ? (
                    <>
                      <select
                        className="rounded-md border border-border px-2 py-1 text-sm"
                        value={item.result ?? ''}
                        onChange={(e) =>
                          void handleChecklistResult(item.id, e.target.value || '')
                        }
                        disabled={submitting}
                      >
                        <option value="">Pendiente</option>
                        <option value="conforme">Conforme</option>
                        <option value="no_conforme">No conforme</option>
                        <option value="no_aplica">No aplica</option>
                      </select>
                      <textarea
                        className="w-full rounded-md border border-border px-3 py-2 text-sm min-h-[60px]"
                        placeholder="Evidencia u observaciones (opcional)"
                        defaultValue={item.evidence ?? ''}
                        onBlur={(e) => {
                          const next = e.target.value;
                          if (next !== (item.evidence ?? '')) {
                            void handleChecklistEvidence(item.id, next);
                          }
                        }}
                      />
                    </>
                  ) : (
                    <>
                      {item.result && (
                        <p className="text-muted-foreground">
                          Resultado: {CHECKLIST_RESULT_LABEL[item.result]}
                        </p>
                      )}
                      {item.evidence && (
                        <p className="text-muted-foreground whitespace-pre-wrap">
                          Evidencia: {item.evidence}
                        </p>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'hallazgos' && (
        <div className="space-y-4">
          {canEdit && (
            <form
              className="border border-border rounded-lg p-4 space-y-3"
              onSubmit={(e) => void handleAddFinding(e)}
            >
              <h3 className="font-medium text-sm">Registrar hallazgo</h3>
              <div className="flex flex-wrap gap-3">
                <select
                  className="rounded-md border border-border px-3 py-2 text-sm"
                  value={findingType}
                  onChange={(e) => setFindingType(e.target.value as FindingType)}
                >
                  {FINDING_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                {findingType === 'no_conformidad' && (
                  <select
                    className="rounded-md border border-border px-3 py-2 text-sm"
                    value={findingSeverity}
                    onChange={(e) => setFindingSeverity(e.target.value as 'mayor' | 'menor')}
                  >
                    <option value="mayor">Mayor</option>
                    <option value="menor">Menor</option>
                  </select>
                )}
                <input
                  className="rounded-md border border-border px-3 py-2 text-sm"
                  placeholder="Cláusula"
                  value={findingClause}
                  onChange={(e) => setFindingClause(e.target.value)}
                />
              </div>
              <textarea
                className="w-full rounded-md border border-border px-3 py-2 text-sm min-h-[80px]"
                placeholder="Descripción"
                value={findingDescription}
                onChange={(e) => setFindingDescription(e.target.value)}
                required
              />
              <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>
                Agregar hallazgo
              </button>
            </form>
          )}
          <AuditFindingsTab
            findings={audit.findings}
            canEdit={canEdit}
            escalatingId={escalatingId}
            promotingId={promotingId}
            onEscalate={(f) => void handleEscalate(f.id)}
            onPromoteToRisk={(f) => void handlePromoteToRisk(f.id)}
          />
        </div>
      )}

      {tab === 'informe' && (
        <div className="space-y-4 text-sm">
          {aiDraftText && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 dark:border-amber-900 dark:bg-amber-950/40">
              <p className="text-xs font-medium text-amber-900 dark:text-amber-100 mb-2">
                Borrador IA — revise antes de usar en el informe formal
              </p>
              <p className="whitespace-pre-wrap text-sm">{aiDraftText}</p>
            </div>
          )}
          {!report ? (
            <p className="text-muted-foreground">Generando informe…</p>
          ) : (
            <>
              <p className="text-muted-foreground">
                Generado: <DateCell value={report.generatedAt} />
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="border border-border rounded-lg p-4">
                  <h3 className="font-medium mb-2">Resumen checklist</h3>
                  <ul className="space-y-1">
                    <li>Total: {report.checklistSummary.total}</li>
                    <li>Conforme: {report.checklistSummary.conforme}</li>
                    <li>No conforme: {report.checklistSummary.noConforme}</li>
                    <li>No aplica: {report.checklistSummary.noAplica}</li>
                    <li>Pendiente: {report.checklistSummary.pendiente}</li>
                  </ul>
                </div>
                <div className="border border-border rounded-lg p-4">
                  <h3 className="font-medium mb-2">Hallazgos por tipo</h3>
                  <ul className="space-y-1">
                    {Object.entries(report.findingsByType).map(([type, count]) => (
                      <li key={type}>
                        {type}: {count}
                      </li>
                    ))}
                    {Object.keys(report.findingsByType).length === 0 && (
                      <li className="text-muted-foreground">Sin hallazgos</li>
                    )}
                  </ul>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
